/**
 * MonthlyBillsChart - Grouped bar chart showing monthly energy costs per building.
 *
 * Displays:
 *   - Monthly energy bills for each building
 *   - Last 6 months of historical data
 *   - Color-coded by building
 *
 * Data sources (in priority order):
 *   1. `data` prop with pre-computed billing data { months, buildings }
 *   2. Empty state when no data is available
 *
 * Props:
 *   - data: Optional external billing data { months: string[], buildings: { name, data }[] }
 *   - buildings: List of buildings for color mapping
 *   - communityTariffs: Optional { importTariff: number } from community metadata
 *   - currency: Currency symbol (default: 'EUR')
 */

import React, { useMemo } from 'react'
import Chart from 'react-apexcharts'

const DEFAULT_COLORS = ['#8CB808', '#5C5C5C', '#6DB1BF', '#9B59B6', '#C73E1D', '#2E86AB', '#A23B72', '#F18F01']

function MonthlyBillsChart({ data, buildings = [], communityTariffs, currency = 'EUR' }) {
  const chartData = useMemo(() => {
    if (data && data.buildings && data.buildings.length > 0) return data
    return null
  }, [data])

  if (!chartData) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: 320, color: 'var(--color-neutral-500)', fontSize: 'var(--font-size-sm)' }}
      >
        No billing data available. Upload historical load datasets to calculate monthly energy bills.
      </div>
    )
  }

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 320,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      animations: { enabled: true, speed: 800 },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '70%',
        borderRadius: 4,
        dataLabels: { position: 'top' },
      },
    },
    colors: buildings.length > 0 ? buildings.map(b => b.color) : DEFAULT_COLORS,
    dataLabels: { enabled: false },
    stroke: { show: true, width: 1, colors: ['transparent'] },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '13px',
      fontFamily: 'var(--font-family-base)',
      markers: { width: 12, height: 12, radius: 3 },
      itemMargin: { horizontal: 15, vertical: 5 },
    },
    xaxis: {
      categories: chartData.months,
      title: {
        text: 'Month',
        style: { fontSize: '13px', fontWeight: 500 },
      },
      labels: { style: { fontSize: '12px', fontWeight: 500 } },
      axisBorder: { show: true, color: '#e0e0e0' },
      axisTicks: { show: true, color: '#e0e0e0' },
    },
    yaxis: {
      title: {
        text: `Monthly Cost (${currency})`,
        style: { fontSize: '13px', fontWeight: 500 },
      },
      labels: {
        style: { fontSize: '12px' },
        formatter: (value) => value != null ? `${currency} ${Math.round(value)}` : '',
      },
      min: 0,
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value) => value != null ? `${currency} ${value.toFixed(2)}` : '',
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
  }

  const series = chartData.buildings

  return (
    <div>
      <div
        className="mb-3 d-flex justify-content-between align-items-center"
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-neutral-600)',
        }}
      >
        <span>Historical energy costs by building</span>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
          Based on actual consumption and tariffs
        </span>
      </div>
      <Chart options={chartOptions} series={series} type="bar" height={320} />
    </div>
  )
}

export default MonthlyBillsChart
